import { ReactElement, Ref, forwardRef } from 'react';
import {
    Box,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableContainer,
    TableRow,
    Paper,
    Typography,
    Dialog,
    tableCellClasses,
    styled,
    IconButton,
    Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LinkIcon from '@mui/icons-material/Link';
import { TransitionProps } from '@mui/material/transitions';
import formatAmounts from 'src/utils/formatAmounts';
import PaymentMethodIcon, {
    PaymentMethod
} from 'src/content/management/Events/components/PaymentMethodIcon';
import {
    PaymentMethodFilterValue
} from 'src/components/Filters/FilterContainerPaymentMethod';
import { matchesPaymentMethodFilter } from 'src/utils/paymentMethodBucket';
import { BoleteriaUserStats } from 'src/models/statistic';

const DialogWrapper = styled(Dialog)(() => `
  .MuiDialog-paper {
    overflow: visible;
  }
`);

const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#F8F8F8',
        color: '#223354',
        fontWeight: 700
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },
    '&:last-child td, &:last-child th': {
        border: 0
    }
}));

const Transition = forwardRef(function Transition(
    props: TransitionProps & { children: ReactElement<any, any> },
    ref: Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

type ModalBoleteriaProps = {
    open: boolean;
    onClose: () => void;
    statistic: BoleteriaUserStats | null;
    initialPaymentFilter: PaymentMethodFilterValue;
};

export const ModalBoleteria = ({
    open,
    onClose,
    statistic,
    initialPaymentFilter
}: ModalBoleteriaProps) => {
    const { t }: { t: any } = useTranslation();

    // Usar directamente el filtro que viene del padre (solo lectura)
    const paymentFilter = initialPaymentFilter;

    const filteredDetailByType = (() => {
        const source = statistic?.detailByType ?? [];

        const filtered = source.filter((item) =>
            matchesPaymentMethodFilter(
                item.tktPaymentMethod as PaymentMethod | null | undefined,
                paymentFilter,
            ),
        );

        const grouped = new Map<string, any>();

        filtered.forEach((item) => {
            const key = `${item.tktProducerName ?? ''}|${item.tktPaymentMethod ?? 'none'}`;

            if (!grouped.has(key)) {
                grouped.set(key, { ...item });
                return;
            }

            const acc = grouped.get(key);

            acc.totalQt = Number(acc.totalQt ?? 0) + Number(item.totalQt ?? 0);
            acc.totalAmount = Number(acc.totalAmount ?? 0) + Number(item.totalAmount ?? 0);
        });
        return Array.from(grouped.values());
    })();

    return (
        <DialogWrapper
            open={open}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Transition}
            onClose={onClose}
            sx={{
                '& .MuiDialog-paper': {
                    maxHeight: 'auto',
                    overflowY: 'auto',
                    '@media (max-width: 400px)': {
                        minWidth: '100vw'
                    }
                }
            }}
        >
            {/* Header con avatar + nombre + X */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                position="sticky"
                top={0}
                zIndex={1}
                bgcolor="background.paper"
                px={2}
                py={2}
            >
                <Box display="flex" alignItems="center" columnGap={1.5}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: '#E8F0FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <PersonOutlineIcon
                            sx={{
                                fontSize: 24,
                                color: '#2F80ED'
                            }}
                        />
                    </Box>

                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            fontSize: 16,
                            lineHeight: '100%',
                            color: '#223354'
                        }}
                    >
                        {statistic
                            ? `${statistic.name} ${statistic.lastname}`
                            : ''}
                    </Typography>
                </Box>

                <IconButton
                    onClick={onClose}
                    sx={{
                        borderRadius: '50%',
                        border: 'none',
                        padding: 0.75,
                        backgroundColor: 'white',
                        '&:hover': {
                            backgroundColor: '#f5f5f5'
                        },
                        width: 36,
                        height: 36
                    }}
                >
                    <CloseIcon sx={{ color: 'gray' }} />
                </IconButton>
            </Box>

            <Divider />

            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                p={2}
            >
                <Box p={2}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1
                        }}
                    >
                        <Typography align="left" variant="h4" sx={{ py: 1, px: 0 }}>
                            {t('Ticket office sale')}
                        </Typography>

                        {
                        <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                                minWidth: 100,
                                height: 40,
                                columnGap: 1.5
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 700,
                                    lineHeight: '100%',
                                    color: '#223354'
                                }}
                            >
                                {t('Payment method')}
                            </Typography>

                            <Box display="flex" columnGap={1}>
                                <IconButton
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        borderWidth: 1,
                                        borderStyle: 'solid',
                                        borderColor: paymentFilter.qr ? '#1A75FF' : '#C5CAD1',
                                        backgroundColor: paymentFilter.qr ? '#E7F0FF' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                        cursor: 'default',
                                        '& svg': {
                                            fontSize: 24,
                                            color: paymentFilter.qr ? '#1A75FF' : '#C5CAD1'
                                        },
                                        '&:hover': {
                                            backgroundColor: paymentFilter.qr ? '#E7F0FF' : 'transparent'
                                        }
                                    }}
                                >
                                    <QrCode2Icon />
                                </IconButton>

                                <IconButton
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        borderWidth: 1,
                                        borderStyle: 'solid',
                                        borderColor: paymentFilter.cash ? '#1A75FF' : '#C5CAD1',
                                        backgroundColor: paymentFilter.cash ? '#E7F0FF' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                        cursor: 'default',
                                        '& svg': {
                                            fontSize: 24,
                                            color: paymentFilter.cash ? '#44D600' : '#C5CAD1'
                                        },
                                        '&:hover': {
                                            backgroundColor: paymentFilter.cash ? '#E7F0FF' : 'transparent'
                                        }
                                    }}
                                >
                                    <MonetizationOnIcon />
                                </IconButton>

                                <IconButton
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        borderWidth: 1,
                                        borderStyle: 'solid',
                                        borderColor: paymentFilter.payment_link ? '#1A75FF' : '#C5CAD1',
                                        backgroundColor: paymentFilter.payment_link ? '#E7F0FF' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                        cursor: 'default',
                                        '& svg': {
                                            fontSize: 24,
                                            color: paymentFilter.payment_link ? '#FFA31A' : '#C5CAD1'
                                        },
                                        '&:hover': {
                                            backgroundColor: paymentFilter.payment_link ? '#E7F0FF' : 'transparent'
                                        }
                                    }}
                                >
                                    <LinkIcon />
                                </IconButton>

                                <IconButton
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        borderWidth: 1,
                                        borderStyle: 'solid',
                                        borderColor: paymentFilter.other ? '#1A75FF' : '#C5CAD1',
                                        backgroundColor: paymentFilter.other ? '#E7F0FF' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                        cursor: 'default',
                                        '& svg': {
                                            fontSize: 24,
                                            color: paymentFilter.other ? '#636161' : '#C5CAD1'
                                        },
                                        '&:hover': {
                                            backgroundColor: paymentFilter.other ? '#E7F0FF' : 'transparent'
                                        }
                                    }}
                                >
                                    <CardTravelIcon />
                                </IconButton>
                            </Box>
                        </Box>                             
                        }

                    </Box>

                    <TableContainer component={Paper}>
                        <Table
                            sx={{ width: '100%', minWidth: { sm: 500 } }}
                            aria-label="customized table"
                        >
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align="left">
                                        {t('Ticket')}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        {t('Quantity')}
                                    </StyledTableCell>
                                    <StyledTableCell align="center" sx={{ width: 150 }}>
                                        {t('Payment method')}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        {t('Amount')}
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            {
                         <TableBody>
                                {statistic && filteredDetailByType.length
                                    ? filteredDetailByType.map((item) => (
                                        <StyledTableRow
                                            key={`${item.tktProducerName}-${item.tktPaymentMethod ?? 'none'}`}
                                        >
                                            <StyledTableCell align="left">
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: 14,
                                                        lineHeight: '100%',
                                                        color: '#223354'
                                                    }}
                                                >
                                                    {item.tktProducerName}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                {item.totalQt}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={{ width: 150 }}>
                                                <PaymentMethodIcon method={item.tktPaymentMethod} />
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                {formatAmounts(item.totalAmount, t)}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                    : null}
                            </TableBody>
                            }

                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </DialogWrapper>
    );
};

export default ModalBoleteria;
